import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CollapsibleSidebar } from "./CollapsibleSidebar";
import {
  HomeIcon,
  FileTextIcon,
  DashboardIcon,
  BarChartIcon,
  QuestionMarkCircledIcon,
  LightningBoltIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { ProfileService } from "../../services/profile.service";
import { hasAccess, FEATURE_ROLES } from "../../utils/roles";

export const NavigationSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const profile = ProfileService.getUserProfileFromStorage();
  const userRoles: string[] = profile?.roles || [];

  // Check access for each feature
  const canSeeDashboard = hasAccess(userRoles, FEATURE_ROLES.DASHBOARD);
  const canSeeCustomReporting = hasAccess(userRoles, FEATURE_ROLES.CUSTOM_REPORTING);
  const canSeeARManagement = hasAccess(userRoles, FEATURE_ROLES.AR_MANAGEMENT);
  const canSeeTrueRateGenerator = hasAccess(userRoles, FEATURE_ROLES.TRUERATE_GENERATOR);
  const canSeeClaimFlow = hasAccess(userRoles, FEATURE_ROLES.CLAIM_FLOW);

  return (
    <CollapsibleSidebar>
      {(isCollapsed) => (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate("/");
            }}
            className={`w-full flex items-center gap-3 text-black text-base font-medium transition-all relative border-b-2 ${
              isActive("/")
                ? "bg-default-99 border-b-border-gray"
                : "hover:bg-gray-200 border-b-transparent"
            } ${isCollapsed ? "px-2 py-4 justify-center" : "px-6 py-4"}`}
            title={isCollapsed ? "Home" : ""}
          >
            {isCollapsed ? (
              <HomeIcon className="h-6 w-5 flex-shrink-0" />
            ) : (
              <span className="font-bold">Home</span>
            )}
          </button>
          {canSeeDashboard && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/dashboards");
              }}
              className={`w-full flex items-center gap-3 text-black font-medium transition-all text-base border-t-2 border-b-2 ${
                isActive("/dashboards")
                  ? "bg-default-99 border-t-border-gray border-b-border-gray"
                  : "hover:bg-gray-200 border-t-transparent border-b-transparent"
              } ${isCollapsed ? "px-2 p-[18px] justify-center" : "px-6 py-4"}`}
              title={isCollapsed ? "Dashboard" : ""}
            >
              {isCollapsed ? (
                <DashboardIcon className="h-5 w-5 flex-shrink-0" />
              ) : (
                <span className="font-bold">Dashboard</span>
              )}
            </button>
          )}
          {canSeeCustomReporting && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/custom-reporting");
              }}
              className={`w-full flex items-center gap-3 text-black font-medium transition-all text-base border-t-2 border-b-2 ${
                isActive("/custom-reporting")
                  ? "bg-default-99 border-t-border-gray border-b-border-gray"
                  : "hover:bg-gray-200 border-t-transparent border-b-transparent"
              } ${isCollapsed ? "px-2 p-[18px] justify-center" : "px-6 py-4"}`}
              title={isCollapsed ? "Custom Reporting" : ""}
            >
              {isCollapsed ? (
                <BarChartIcon className="h-5 w-5 flex-shrink-0" />
              ) : (
                <span className="font-bold">Custom Reporting</span>
              )}
            </button>
          )}
          {canSeeARManagement && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/ar-management");
              }}
              className={`w-full flex items-center gap-3 text-black font-medium transition-all text-base border-t-2 border-b-2 ${
                isActive("/ar-management")
                  ? "bg-default-99 border-t-border-gray border-b-border-gray"
                  : "hover:bg-gray-200 border-t-transparent border-b-transparent"
              } ${isCollapsed ? "px-2 p-[18px] justify-center" : "px-6 py-4"}`}
              title={isCollapsed ? "A/R Management" : ""}
            >
              {isCollapsed ? (
                <MagnifyingGlassIcon className="h-5 w-5 flex-shrink-0" />
              ) : (
                <span className="font-bold">A/R Management</span>
              )}
            </button>
          )}
          {canSeeTrueRateGenerator && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/truerate-generator");
              }}
              className={`w-full flex items-center gap-3 text-black font-medium transition-all text-base border-t-2 border-b-2 ${
                isActive("/truerate-generator")
                  ? "bg-default-99 border-t-border-gray border-b-border-gray"
                  : "hover:bg-gray-200 border-t-transparent border-b-transparent"
              } ${isCollapsed ? "px-2 p-[18px] justify-center" : "px-6 py-4"}`}
              title={isCollapsed ? "TrueRate Generator" : ""}
            >
              {isCollapsed ? (
                <LightningBoltIcon className="h-5 w-5 flex-shrink-0" />
              ) : (
                <span className="font-bold">TrueRate Generator</span>
              )}
            </button>
          )}
          {canSeeClaimFlow && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/claims");
              }}
              className={`w-full flex items-center gap-3 text-black font-medium transition-all text-base border-t-2 border-b-2 ${
                isActive("/claims")
                  ? "bg-default-99 border-t-border-gray border-b-border-gray"
                  : "hover:bg-gray-200 border-t-transparent border-b-transparent"
              } ${isCollapsed ? "px-2 p-[18px] justify-center" : "px-6 py-4"}`}
              title={isCollapsed ? "Claim Flow" : ""}
            >
              {isCollapsed ? (
                <FileTextIcon className="h-5 w-5 flex-shrink-0" />
              ) : (
                <span className="font-bold">Claim Flow</span>
              )}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate("/support");
            }}
            className={`w-full flex items-center gap-3 text-black font-medium transition-all text-base border-t-2 border-b-2 ${
              isActive("/support")
                ? "bg-default-99 border-t-border-gray border-b-border-gray"
                : "hover:bg-gray-200 border-t-transparent border-b-transparent"
            } ${isCollapsed ? "px-2 p-[18px] justify-center" : "px-6 py-4"}`}
            title={isCollapsed ? "Support" : ""}
          >
            {isCollapsed ? (
              <QuestionMarkCircledIcon className="h-5 w-5 flex-shrink-0" />
            ) : (
              <span className="font-bold">Support</span>
            )}
          </button>
        </>
      )}
    </CollapsibleSidebar>
  );
};
